import React, { useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import classNames from 'classnames';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { Page, LayoutSingleColumn, NamedLink } from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './HowItWorksPage.module.css';

const RENT_STEPS = [
  {
    title: 'Browse like it’s already yours',
    text:
      'Find exactly what you need in our closets. Filter by size, occasion, or "will this get a compliment at brunch."',
  },
  {
    title: 'Book it, or buy it',
    text:
      'Renting? Pick your dates. Buying? It’s yours for good. Either way, checkout takes less time than deciding what to wear tomorrow.',
  },
  {
    title: 'It shows up. You look incredible',
    text:
      'Drop-off or ready for pickup, cleaned and pressed. Showing it off is optional, we won’t stop you.',
  },
  {
    title: 'Send it back — or don’t',
    text:
      'Rentals go back by the date on your dashboard. Bought it? Keep it forever. We already knew you would.',
  },
];

const LEND_STEPS = [
  {
    title: 'Snap it, list it',
    text:
      'Photos on your bed, your floor, wherever. Set your own price, or let us suggest one — we’ve seen a lot of closets.',
  },
  {
    title: 'We bring the renters & buyers',
    text:
      'Your piece goes live to everyone browsing. You approve every request — no surprise strangers, no exceptions.',
  },
  {
    title: 'Hand it off',
    text:
      'Ship it, drop it, or arrange a pickup. First time doing this? We’ll walk you through it — it’s less scary than it sounds.',
  },
  {
    title: 'Get paid, automatically',
    text:
      '80% of every rental and sale lands straight in your bank account via Stripe. We keep 20% for keeping the lights on.',
  },
];

const TRUST_ITEMS = [
  {
    label: 'Sellers & lenders ID-verified',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 3l7 3v6c0 4.5-3 8-7 9-4-1-7-4.5-7-9V6l7-3z" />
        <path d="M9 12l2 2 4-4" />
      </svg>
    ),
  },
  {
    label: 'Payments secured by Stripe',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2.5" y="5.5" width="19" height="13" rx="2" />
        <path d="M2.5 9.5h19" />
      </svg>
    ),
  },
  {
    label: 'Based in BC, Canada',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 21s7-6.1 7-11.5A7 7 0 0 0 5 9.5C5 14.9 12 21 12 21z" />
        <circle cx="12" cy="9.5" r="2.3" />
      </svg>
    ),
  },
  {
    label: 'Real humans if something goes sideways',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 11.5a8.38 8.38 0 0 1-8.5 8.4 8.5 8.5 0 0 1-4-1L3 20l1.1-4.5a8.4 8.4 0 0 1-1-4A8.38 8.38 0 0 1 11.6 3a8.5 8.5 0 0 1 9.4 8.5z" />
      </svg>
    ),
  },
];

const StepRail = props => {
  const { steps } = props;
  return (
    <div className={css.rail}>
      {steps.map((step, index) => (
        <div className={css.step} key={step.title}>
          <span className={css.num}>{String(index + 1).padStart(2, '0')}</span>
          <h3 className={css.stepTitle}>{step.title}</h3>
          <p className={css.stepText}>{step.text}</p>
        </div>
      ))}
    </div>
  );
};

export const HowItWorksPageComponent = props => {
  const { scrollingDisabled } = props;
  const [mode, setMode] = useState('rent');
  const isRent = mode === 'rent';

  return (
    <Page
      title="How It Works | Nothing to Wear"
      description="See how renting, buying, lending, and selling clothing works on Nothing to Wear — list an item in minutes, or find your next outfit nearby."
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.page} data-mode={mode}>
          <div className={css.backLinkRow}>
            <NamedLink name="SearchPage" className={css.backLink}>
              Back to shop
            </NamedLink>
          </div>

          <section className={css.hero}>
            <span className={css.eyebrow}>How it works</span>
            <h1 className={css.heroTitle}>
              Nothing to wear? <i className={css.heroTitleItalic}>Obviously false.</i>
            </h1>
            <p className={css.subhead}>
              You have a closet full of options. You&rsquo;re just tired of all of them.
              Rent something new for that thing you&rsquo;re attending, buy something someone else got tired of,
              or hand off the pieces you&rsquo;re done with &mdash; to someone who isn&rsquo;t.
            </p>

            <div className={css.toggleStage}>
              <div>
                <div className={css.tagToggle} role="group" aria-label="Choose how you want to use the marketplace">
                  <div
                    className={classNames(css.thumb, { [css.thumbLend]: !isRent })}
                    aria-hidden="true"
                  />
                  <button
                    type="button"
                    className={css.toggleButton}
                    aria-pressed={isRent}
                    onClick={() => setMode('rent')}
                  >
                    Rent &amp; buy
                  </button>
                  <button
                    type="button"
                    className={css.toggleButton}
                    aria-pressed={!isRent}
                    onClick={() => setMode('lend')}
                  >
                    Lend &amp; sell
                  </button>
                </div>
                <p className={css.toggleHint}>
                  {isRent ? 'Showing: how renting & buying works' : 'Showing: how lending & selling works'}
                </p>
              </div>
            </div>
          </section>

          <section className={css.steps}>
            {isRent ? (
              <div className={css.stepsHead}>
                <h2 className={css.stepsHeading}>Fixing &ldquo;nothing to wear,&rdquo; in four easy steps</h2>
                <p className={css.stepsSubheading}>
                  No regret purchases. No pretending you&rsquo;ll wear it &ldquo;someday.&rdquo;
                </p>
              </div>
            ) : (
              <div className={css.stepsHead}>
                <h2 className={css.stepsHeading}>Emptying your &ldquo;nothing to wear&rdquo; closet, in four easy steps</h2>
                <p className={css.stepsSubheading}>
                  You have to work. Your clothes should have a job too.
                </p>
              </div>
            )}

            <StepRail steps={isRent ? RENT_STEPS : LEND_STEPS} />
          </section>

          <section className={css.trust}>
            {TRUST_ITEMS.map(item => (
              <div className={css.trustItem} key={item.label}>
                <span className={css.trustIcon}>{item.icon}</span>
                <span>{item.label}</span>
              </div>
            ))}
          </section>

          <section className={css.cta}>
            <h2 className={css.ctaHeading}>
              Fix &ldquo;nothing to wear&rdquo; <i className={css.ctaHeadingItalic}>for good.</i>
            </h2>
            <p className={css.ctaText}>
              Start renting, buying, lending, or selling &mdash; in about the time it takes to stare
              into your closet and sigh.
            </p>
            <div className={css.ctaButtons}>
              <NamedLink name="SearchPage" className={classNames(css.btn, css.btnSolid)}>
                Start renting &amp; buying
              </NamedLink>
              <NamedLink name="NewListingPage" className={classNames(css.btn, css.btnGhost)}>
                Start lending &amp; selling
              </NamedLink>
            </div>
          </section>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => {
  return {
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const HowItWorksPage = compose(connect(mapStateToProps))(HowItWorksPageComponent);

export default HowItWorksPage;
